import { Alert } from "@/src/lib/alert";
import { Text } from "@/src/components/ui/BrandText";
import { Ionicons } from "@/src/components/ui/ionicons";
import { useActiveAccount } from "@/src/hooks/useActiveAccount";
import { useAuth } from "@/src/hooks/useAuth";
import { createChatMessageId, sendSharedPostMessage } from "@/src/lib/chat";
import { startIdentityDm, sendIdentityDmMessage } from "@/src/lib/directMessaging";
import { db } from "@/src/lib/firebase";
import { friendlyFirebaseError } from "@/src/lib/friendlyFirebaseError";
import { buildSharedPostPayload } from "@/src/lib/sharedPost";
import { border, surface, text as uiText, brand } from "@/src/theme/tokens";
import { router, useLocalSearchParams } from "expo-router";
import { collection, doc, getDoc, getDocs, limit, query, where } from "firebase/firestore";
import React, { useEffect, useMemo, useState } from "react";
import { ActivityIndicator, FlatList, Pressable, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

const BG = surface.secondary;
const CARD = surface.primary;
const BORDER = border.subtle;
const MUTED = uiText.muted;

type GroupConversation = {
  id: string;
  type?: "dm" | "group";
  members?: string[];
  name?: string;
  title?: string;
};

type IdentityTarget = {
  id: string;
  accountType?: "student" | "teacher" | "institution";
  username?: string;
  displayName?: string;
  institutionName?: string;
};

function uniq(items: string[]) {
  return Array.from(new Set(items.filter(Boolean)));
}

function targetLabel(item: IdentityTarget) {
  return item.displayName?.trim()
    || item.institutionName?.trim()
    || (item.username ? `@${item.username.replace(/^@/, "")}` : "Identité Neqraw");
}

export default function SharePostScreen() {
  const { user } = useAuth();
  const { activeAccountId, activeAccount } = useActiveAccount();
  const { postId } = useLocalSearchParams<{ postId: string }>();
  const pid = useMemo(() => String(postId || ""), [postId]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const [sharingId, setSharingId] = useState("");
  const [post, setPost] = useState<any>(null);
  const [groups, setGroups] = useState<GroupConversation[]>([]);
  const [identities, setIdentities] = useState<IdentityTarget[]>([]);
  const [allowedMemberIds, setAllowedMemberIds] = useState<string[]>([]);
  const [currentGroupIsPublic, setCurrentGroupIsPublic] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!user?.uid || !activeAccountId || !pid) {
        if (!cancelled) setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setLoadError(null);
        const postSnap = await getDoc(doc(db, "posts", pid));
        if (!postSnap.exists()) {
          if (!cancelled) {
            setPost(null);
            setLoading(false);
          }
          return;
        }
        const postData = { id: postSnap.id, ...(postSnap.data() as any) };
        let privateGroupMemberIds: string[] = [];
        let groupIsPublicNow: boolean | null = null;

        if (postData.visibility === "group" && postData.groupId) {
          const groupRef = doc(db, "groups", String(postData.groupId));
          const groupSnap = await getDoc(groupRef);
          groupIsPublicNow = groupSnap.exists() ? groupSnap.data()?.isPublic === true : null;
          if (groupSnap.exists() && groupIsPublicNow === false) {
            const membersSnap = await getDocs(query(collection(groupRef, "members"), limit(300)));
            privateGroupMemberIds = uniq([
              String(groupSnap.data()?.ownerUid || ""),
              ...membersSnap.docs.map((memberDoc) => memberDoc.id),
            ]);
          }
        }

        const [convSnap, accountSnap] = await Promise.all([
          getDocs(query(collection(db, "conversations"), where("members", "array-contains", user.uid), limit(100))),
          getDocs(query(collection(db, "accounts"), where("status", "==", "active"), limit(200))),
        ]);
        if (cancelled) return;

        const groupItems = convSnap.docs
          .map((item) => ({ id: item.id, ...(item.data() as any) } as GroupConversation))
          .filter((item) => item.type === "group" && Array.isArray(item.members) && item.members.includes(user.uid))
          .filter((item) => !privateGroupMemberIds.length || (item.members || []).every((uid) => privateGroupMemberIds.includes(uid)))
          .sort((a, b) => String(a.name || a.title || a.id).localeCompare(String(b.name || b.title || b.id), "fr", { sensitivity: "base" }));

        const identityItems = accountSnap.docs
          .map((item) => ({ id: item.id, ...(item.data() as any) } as IdentityTarget))
          .filter((item) => item.id !== activeAccountId)
          .sort((a, b) => targetLabel(a).localeCompare(targetLabel(b), "fr", { sensitivity: "base" }));

        setPost(postData);
        setGroups(groupItems);
        setIdentities(identityItems);
        setAllowedMemberIds(privateGroupMemberIds);
        setCurrentGroupIsPublic(groupIsPublicNow);
        setLoading(false);
      } catch {
        if (!cancelled) {
          setLoading(false);
          setLoadError("Le partage n’a pas pu être chargé. Vérifie ta connexion puis réessaie.");
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [activeAccountId, pid, retryKey, user?.uid]);

  const isPrivateGroupPost = useMemo(
    () => post?.visibility === "group" && !!post?.groupId && currentGroupIsPublic === false,
    [currentGroupIsPublic, post]
  );

  async function shareToGroup(item: GroupConversation) {
    if (!user?.uid || !post || sharingId) return;
    if (isPrivateGroupPost && !(item.members || []).every((uid) => allowedMemberIds.includes(uid))) {
      Alert.alert("Partage limité", "Ce post privé ne peut être partagé qu’avec des membres du groupe.");
      return;
    }
    try {
      setSharingId(`group:${item.id}`);
      await sendSharedPostMessage(item.id, user.uid, buildSharedPostPayload(post));
      router.replace({ pathname: "/(tabs)/messages/[id]", params: { id: item.id } });
    } catch (error) {
      Alert.alert("Erreur", friendlyFirebaseError(error, "Impossible de partager ce post."));
    } finally {
      setSharingId("");
    }
  }

  async function shareToIdentity(item: IdentityTarget) {
    if (!activeAccountId || !post || sharingId) return;
    try {
      setSharingId(`identity:${item.id}`);
      const started = await startIdentityDm(activeAccountId, item.id);
      await sendIdentityDmMessage({
        conversationId: started.conversation.id,
        senderIdentityId: activeAccountId,
        messageId: createChatMessageId(),
        type: "shared_post",
        text: "Publication partagée",
        postId: post.id,
      });
      router.replace({ pathname: "/(tabs)/messages/direct/[id]", params: { id: started.conversation.id } });
    } catch (error) {
      Alert.alert("Erreur", friendlyFirebaseError(error, "Impossible d’envoyer ce post en message direct."));
    } finally {
      setSharingId("");
    }
  }

  if (loading) {
    return <SafeAreaView style={{ flex: 1, backgroundColor: BG }}><View style={{ flex: 1, alignItems: "center", justifyContent: "center" }}><ActivityIndicator color={uiText.primary} /></View></SafeAreaView>;
  }

  if (loadError || !post || !activeAccountId) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: BG }}>
        <View style={{ flex: 1, padding: 16 }}>
          <Pressable onPress={() => router.back()} style={{ width: 40, height: 40, borderRadius: 12, backgroundColor: CARD, borderWidth: 1, borderColor: BORDER, alignItems: "center", justifyContent: "center" }}><Ionicons name="chevron-back" size={20} color={uiText.primary} /></Pressable>
          <View style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
            <Text style={{ color: uiText.primary, fontSize: 18, fontWeight: "900" }}>{!activeAccountId ? "Aucune identité active" : loadError ? "Partage indisponible" : "Post introuvable"}</Text>
            {loadError ? <Pressable onPress={() => setRetryKey((value) => value + 1)} style={{ padding: 16 }}><Text style={{ color: uiText.brand }}>Réessayer</Text></Pressable> : null}
          </View>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: BG }}>
      <FlatList
        data={identities}
        keyExtractor={(item) => item.id}
        contentContainerStyle={{ padding: 16, paddingBottom: 28 }}
        ItemSeparatorComponent={() => <View style={{ height: 10 }} />}
        ListHeaderComponent={
          <View style={{ paddingBottom: 16 }}>
            <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
              <Pressable onPress={() => router.back()} style={{ width: 40, height: 40, borderRadius: 12, backgroundColor: CARD, borderWidth: 1, borderColor: BORDER, alignItems: "center", justifyContent: "center" }}><Ionicons name="chevron-back" size={20} color={uiText.primary} /></Pressable>
              <View style={{ flex: 1 }}><Text style={{ color: uiText.primary, fontSize: 22, fontWeight: "900" }}>Partager le post</Text><Text style={{ color: MUTED, marginTop: 4 }} numberOfLines={2}>En tant que {activeAccount?.displayName || "ton identité active"}</Text></View>
            </View>

            {isPrivateGroupPost ? <View style={{ marginTop: 16, backgroundColor: surface.warning, borderWidth: 1, borderColor: brand.accent, borderRadius: 14, padding: 12 }}><Text style={{ color: uiText.primary, fontWeight: "800" }}>Post de groupe privé</Text><Text style={{ color: MUTED, marginTop: 4, lineHeight: 20 }}>Le backend vérifie que le destinataire a réellement accès au groupe avant l&apos;envoi.</Text></View> : null}

            <View style={{ marginTop: 20 }}><Text style={{ color: uiText.primary, fontSize: 18, fontWeight: "900" }}>Groupes</Text><View style={{ marginTop: 10, gap: 10 }}>{groups.length ? groups.map((item) => {
              const busy = sharingId === `group:${item.id}`;
              return <Pressable key={item.id} onPress={() => shareToGroup(item)} disabled={!!sharingId} style={{ backgroundColor: CARD, borderWidth: 1, borderColor: BORDER, borderRadius: 14, padding: 14, opacity: sharingId && !busy ? 0.55 : 1 }}><Text style={{ color: uiText.primary, fontWeight: "800" }}>{item.name || item.title || "Groupe"}</Text><Text style={{ color: MUTED, marginTop: 4 }}>{busy ? "Envoi…" : "Partager dans ce groupe"}</Text></Pressable>;
            }) : <View style={{ backgroundColor: CARD, borderWidth: 1, borderColor: BORDER, borderRadius: 14, padding: 14 }}><Text style={{ color: MUTED }}>Aucun groupe compatible.</Text></View>}</View></View>

            <View style={{ marginTop: 20, marginBottom: 12 }}><Text style={{ color: uiText.primary, fontSize: 18, fontWeight: "900" }}>Messages directs</Text><Text style={{ color: MUTED, marginTop: 4 }}>Le destinataire recevra le post dans l&apos;inbox de l&apos;identité ciblée.</Text></View>
          </View>
        }
        ListEmptyComponent={<View style={{ backgroundColor: CARD, borderWidth: 1, borderColor: BORDER, borderRadius: 14, padding: 14 }}><Text style={{ color: MUTED }}>Aucune identité compatible avec ce partage.</Text></View>}
        renderItem={({ item }) => {
          const busy = sharingId === `identity:${item.id}`;
          return <Pressable onPress={() => shareToIdentity(item)} disabled={!!sharingId} style={{ backgroundColor: CARD, borderWidth: 1, borderColor: BORDER, borderRadius: 14, padding: 14, opacity: sharingId && !busy ? 0.55 : 1 }}><Text style={{ color: uiText.primary, fontWeight: "800" }}>{targetLabel(item)}</Text><Text style={{ color: MUTED, marginTop: 4 }}>{busy ? "Envoi…" : item.accountType === "institution" ? "Envoyer à cette école" : "Envoyer en message direct"}</Text></Pressable>;
        }}
      />
    </SafeAreaView>
  );
}
